import React, { useCallback, useEffect, useMemo, useState } from 'react'
import classNames from 'classnames'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import ReactPlaceholder from 'react-placeholder'

import Header from './components/Header'
import Manual from './components/Manual'
import Container from './components/Container'
import SectionContentMobile from './components/SectionContentMobile'
import ManualSections from './components/ManualSections'
import SectionContent from './components/SectionContent'
import useHomeManuals from './hooks/useHomeManuals'
import Placeholder from './components/Placeholders'
import useRedirectToManual from './hooks/useRedirectToManual'
import useSetLastId from './hooks/useSetLastId'
import { getImageSize, getThumbnailUrl } from './utils/getThumbnailUrl'

import style from './App.module.css'
import { HomeManualFieldProjection, SetStateType } from './definitions'
import { Tracker } from './EventTracking'
import { getSectionContent } from './utils/getters'
import { analytics } from 'utils/analytics'

interface MatchParams {
  homeId: string
  section?: string
}

Tracker.initialize()
Tracker.registerPageView()

function camelCaseToDash(str: any) {
  return str
    .replace(/\//g, '-')
    .replace(/([a-zA-Z])(?=[A-Z])/g, '$1-')
    .toLowerCase()
}

function App({ history, match }: RouteComponentProps<MatchParams>) {
  const {
    params: { homeId, section: urlSection },
  } = match

  useSetLastId({ homeId })
  useRedirectToManual({ homeId })

  const { loading, error, data } = useHomeManuals({
    homeId,
  })

  const [columnState, setColumnState] = useState('')

  const activeSection = useMemo(() => {
    if (!Object.keys(data).length) return null

    const sections = data.book?.sections
    if (!sections || !sections.length) return null

    return sections.find(section => camelCaseToDash(section.key) === urlSection) || null
  }, [data, urlSection])

  const activeSectionContent = useMemo(() => {
    return loading || error || !activeSection ? '' : getSectionContent(activeSection)
  }, [activeSection, error, loading])

  const columnClassNames = useMemo(() => {
    return classNames({
      [style.column]: true,
      [style.open]: columnState === 'open',
      [style.close]: columnState === 'close',
    })
  }, [columnState])

  const handleOpenSection = useCallback<SetStateType>(
    (nextSection: HomeManualFieldProjection | null) => {
      if (!nextSection) {
        setColumnState('close')
        history.push(`/${homeId}`)
        return
      }

      if (nextSection.key !== urlSection) {
        history.push(`/${homeId}/${camelCaseToDash(nextSection.key)}`)
      }
    },
    [history, homeId, urlSection]
  )

  useEffect(() => {
    if (activeSection) {
      setColumnState('open')
      Tracker.registerSectionOpening(activeSection.title)
      analytics.track('viewed_manual', {
        URL: window.location.href,
        homeId,
        homeName: data.title,
        category: 'manual',
      })
    }
  }, [activeSection, data.title, homeId])

  if (error) {
    return <>error...</>
  }

  return (
    <>
      <div className={style.container}>
        <div className={columnClassNames}>
          <Header />
          <Container className={style.bodyContainer}>
            <ReactPlaceholder ready={!loading} customPlaceholder={<Placeholder />}>
              {!loading && (
                <Manual
                  name={data.title}
                  region={data.region.name}
                  thumbnail={`${getThumbnailUrl({
                    originalSrc: data.thumbnail,
                    ...getImageSize(),
                  })}`}
                >
                  <>
                    <ManualSections
                      sections={data.book.sections}
                      activeSectionTitle={activeSection?.title}
                      setState={handleOpenSection}
                    />
                    <SectionContent
                      content={activeSectionContent}
                      sectionTitle={activeSection?.title}
                    />
                  </>
                </Manual>
              )}
            </ReactPlaceholder>
          </Container>
        </div>
        <div className={`${columnClassNames} ${style.columnRight}`}>
          <SectionContentMobile
            setState={handleOpenSection}
            houseName={data.title}
            activeSectionTitle={activeSection?.title}
            content={activeSectionContent}
          />
        </div>
      </div>
    </>
  )
}

export default withRouter(App)
